import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent
} from '@mui/material';
import { apiClient } from '../../lib/apiClient';
import { formatDate } from '../../lib/dateUtils';
import NoChurchSelected from '../../components/common/NoChurchSelected';

function AnnouncementsView({ user }) {
  const [selectedChurch, setSelectedChurch] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadData = async () => {
      try {
        // Get user's churches first to validate
        const myChurches = await apiClient.getMyChurches(user.id);
        
        if (myChurches.length === 0) {
          localStorage.removeItem('selectedChurch');
          setError('Please join a church first');
          setLoading(false);
          return;
        }

        // Get selected church from localStorage and validate it
        const stored = localStorage.getItem('selectedChurch');
        let church;
        if (stored) {
          const storedChurch = JSON.parse(stored);
          // Verify the stored church is in the user's churches
          const isValidChurch = myChurches.some(c => c.id === storedChurch.id);
          if (isValidChurch) {
            church = storedChurch;
          } else {
            // Invalid church, use first available
            localStorage.removeItem('selectedChurch');
            church = myChurches[0];
            localStorage.setItem('selectedChurch', JSON.stringify(church));
            window.dispatchEvent(new Event('churchSelected'));
          }
        } else {
          // No stored church, use first one
          church = myChurches[0];
          localStorage.setItem('selectedChurch', JSON.stringify(church));
          window.dispatchEvent(new Event('churchSelected'));
        }

        setSelectedChurch(church);
        const data = await apiClient.getAnnouncements(church.id);
        setAnnouncements(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      loadData();
    }
  }, [user]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && error === 'Please join a church first') {
    return <NoChurchSelected user={user} />;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
        Announcements
      </Typography>
      {selectedChurch && (
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
          {selectedChurch.name}
        </Typography>
      )}

      {announcements.length === 0 ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          No announcements yet.
        </Alert>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3 }}>
          {announcements.map((announcement) => (
            <Card key={announcement.id}>
              <CardContent>
                <Typography variant="h6">{announcement.title}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                  {announcement.content}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {formatDate(announcement.createdAt)}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );
}

export default AnnouncementsView;

